import { calculerRemise } from '../utils/promo';
import React, { createContext, useState, useEffect, useMemo } from 'react';

export const CartContext = createContext();

// Prix d'un grattage / d'une personne supplémentaire, en centimes. SEUL endroit où il est écrit :
// la fiche produit et le panier l'importent d'ici.
export const EXTRA_SCRATCH_PRICE_CENTS = 50;

export const CartProvider = ({ children }) => {
  const LOCAL_STORAGE_KEY = "cartData";

  // Récupérer le panier depuis le localStorage au chargement
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem(LOCAL_STORAGE_KEY);
    return savedCart ? JSON.parse(savedCart) : [];
  });

  // Code promo appliqué ({ code, discount_type, discount_value, applicable_a, expire_le }) ; la remise
  // est recalculée à chaque changement du panier (quantités, articles) : src/utils/promo.js.
  const [promo, setPromo] = useState(null);
  const promoCode = promo ? promo.code : '';
  const discount = useMemo(() => calculerRemise(cart, promo), [cart, promo]);

  // Sauvegarder le panier dans le localStorage à chaque modification
  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product) => {
    setCart((prevCart) => [...prevCart, { ...product, quantity: 1 }]);

    // Envoyer l'événement à Google Tag Manager
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "add_to_cart",
      ecommerce: {
        currency: "EUR",
        value: product.price / 100,
        items: [{
          item_name: product.name,
          item_id: product.id,
          price: product.price / 100,
          quantity: 1
        }]
      }
    });
  };

  const updateQuantity = (index, newQuantity) => {
    setCart((prevCart) =>
      prevCart.map((item, i) =>
        i === index ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const updateCartItemQuantity = (index, newQuantity) => {
    const quantityNumber = Number(newQuantity);
    if (!Number.isFinite(quantityNumber) || quantityNumber < 1) return;
    updateQuantity(index, quantityNumber);
  };

  // Grattages / personnes supplémentaires (option payante, indépendante de la quantité) :
  // +0,50 € par personne au-delà des 2 grattages de base. Le prix de l'item est recalculé
  // à partir de basePrice (prix TTC en centimes du produit seul).
  const BASE_SCRATCH_ALLOWANCE = 2;
  const MAX_EXTRA_SCRATCHES = 20;
  const updateCartItemExtras = (index, newExtras) => {
    const extras = Math.max(0, Math.min(MAX_EXTRA_SCRATCHES, Number(newExtras) || 0));
    setCart((prevCart) =>
      prevCart.map((item, i) => {
        if (i !== index) return item;
        const basePrice = item.basePrice ?? (item.price - (item.extraScratches || 0) * EXTRA_SCRATCH_PRICE_CENTS);
        return {
          ...item,
          basePrice,
          extraScratches: extras,
          maxScratchAllowed: BASE_SCRATCH_ALLOWANCE + extras,
          price: basePrice + extras * EXTRA_SCRATCH_PRICE_CENTS,
        };
      })
    );
  };

  const removeFromCart = (index) => {
    setCart((prevCart) => prevCart.filter((_, i) => i !== index));
  };

  const clearCart = () => {
    setCart([]);
    setPromo(null);
  };

  return (
    <CartContext.Provider value={{
      cart, addToCart, updateQuantity, updateCartItemQuantity, updateCartItemExtras, removeFromCart, clearCart,
      promo, setPromo, promoCode, discount
    }}>
      {children}
    </CartContext.Provider>
  );
};
